import React, { useRef } from 'react';

import type { WebappResponse } from '@eventbrite/event-listing-service-client';

import { ArtistsInfo, type ArtistInfo } from '@components/ArtistsInfo/ArtistsInfo';
import { GoodToKnow } from '@/components/GoodToKnow/GoodToKnow';
import { ConversionBar } from '@components/ConversionBar';
import { EventDetails } from '@/components/EventDetails';
import { EventFAQs } from '@components/EventFAQs';
import { EventHero } from '@components/EventHero';
import { EventTags } from '@components/EventTags';
import { Footer } from '@components/Footer';
import { OrganizerCard } from '@components/OrganizerCard';
import { Overview } from '@/components/Overview';
import { RelatedEvents } from '@components/RelatedEvents';
import { MoreOrganizerEvents } from '@components/MoreOrganizerEvents';
import { ReportThisEventButton, type ReportThisEventButtonRef } from '@components/ReportThisEventButton';
import { EventSignalsBar } from '@/components/EventSignalsBar/EventSignalsBar';
import { Agenda } from '@/components/Agenda/Agenda';
import { Tab } from '@/components/Agenda/Content/Tab';

import { ViewDetailsButton } from '@shared/buttons/ViewDetailsButton/ViewDetailsButton';
import { Layout, Main } from '@shared/Layout/Layout';
import { SectionWrapper } from '@shared/SectionWrapper';
import { useAppContext } from '@shared/Wrapper';

import type { BasicInfo, Gallery, Tags, StructuredContent } from '@/types';

import {
  useBodyRendering,
  useEndedEventDetailsVisibility,
  useIntersectionObserver,
  useStickyDesktopConversionBar,
  useTrackingPixels,
} from './hooks';
import { isEventEnded, getCarouselItems, getEventSlugFromBasicInfo } from './utils/bodyUtils';

import styles from './Body.module.scss';

import { Location } from '@/components/Location/Location';
import { useUserData } from '@/hooks/useUserData';
import { getLocationInfoFrom } from './utils/getLocationInfoFrom';
import { SalesStatusProvider } from '@/components/_shared/contexts/SalesStatusContext';
import { Stage } from '@eventbrite/webapp-constants';
import { CreatorBanner } from '../CreatorBanner';
import { OrganizerFollowStatusProvider } from '../_shared/contexts/FollowOrganizerContext';

interface BodyProps {
  basicInfo: BasicInfo;
  gallery: Gallery;
  collections: WebappResponse['collections'];
  tags?: Tags;
  structuredContent?: StructuredContent;
  artistInfo?: ArtistInfo;
  agendaInfo?: { tabs?: Tab[] };
  googleMapsApiKey: string;
  presenterInfo: WebappResponse['presenterInfo'];
  salesStatus?: WebappResponse['salesStatus'];
  stage: Stage;
  goodToKnow: WebappResponse['goodToKnow'];
}

export const Body: React.FC<BodyProps> = ({
  basicInfo,
  collections,
  gallery,
  tags,
  structuredContent,
  agendaInfo,
  artistInfo,
  googleMapsApiKey,
  presenterInfo,
  salesStatus,
  goodToKnow,
  stage,
}) => {
  const reportButtonRef = useRef<ReportThisEventButtonRef>(null);
  const {
    data: { locale },
  } = useAppContext();

  useTrackingPixels({ stage });

  // Custom hooks
  const { sentinelRef, isStickyConversionBar } = useIntersectionObserver();
  const { isBodyRendered } = useBodyRendering();
  const eventEnded = isEventEnded(basicInfo.status);
  const { showEndedEventDetails, expandDetails } = useEndedEventDetailsVisibility(eventEnded);
  const carouselItems = getCarouselItems(gallery);
  const { location, map } = getLocationInfoFrom(basicInfo, googleMapsApiKey);
  const eventSlug = getEventSlugFromBasicInfo(basicInfo);
  const { showAccessLink, userId, sessionId, guestId, isLoading, canManageEvent } = useUserData();

  // Recalculate conversion bar position when CreatorBanner appears (to avoid overlapping).
  const { wrapperRef, isPositionCalculated } = useStickyDesktopConversionBar(!isLoading && canManageEvent);

  const sessionData = isLoading ? undefined : { userId, sessionId, guestId };

  const enrichedLocation = showAccessLink && eventSlug ? { ...location, onlineEventUrl: `/x/${eventSlug}` } : location;

  const featuredVideo =
    gallery?.featuredVideo && gallery.featuredVideo.embedUrl
      ? { url: gallery.featuredVideo.url, embedUrl: gallery.featuredVideo.embedUrl }
      : undefined;

  const nativeVideo =
    gallery?.nativeVideo && gallery.nativeVideo.url
      ? { url: gallery.nativeVideo.url, thumbnail: gallery.nativeVideo.thumbnail || '' }
      : undefined;

  const viewDetailsButton = eventEnded && !showEndedEventDetails ? <ViewDetailsButton onClick={expandDetails} /> : null;

  const overviewComponent = showEndedEventDetails ? (
    <Overview summary={basicInfo.summary} structuredContent={structuredContent} />
  ) : null;

  const artistInfoComponent =
    showEndedEventDetails && artistInfo ? (
      <div className={styles.artistsInfoContainer}>
        <ArtistsInfo info={artistInfo} />
      </div>
    ) : null;

  const goodToKnowComponent = showEndedEventDetails ? (
    <GoodToKnow highlights={goodToKnow?.highlights} refundPolicy={goodToKnow?.refundPolicy} />
  ) : null;

  const locationComponent = showEndedEventDetails ? <Location location={enrichedLocation} map={map} /> : null;

  const agendaComponent = showEndedEventDetails && agendaInfo ? <Agenda tabs={agendaInfo?.tabs} /> : null;

  const faqsComponent = showEndedEventDetails ? <EventFAQs faqs={structuredContent?.widgets?.faqs ?? []} /> : null;

  const organizerCardComponent = showEndedEventDetails ? <OrganizerCard organizer={basicInfo.organizer} /> : null;

  const reportThisEventButtonComponent = showEndedEventDetails ? (
    <ReportThisEventButton
      ref={reportButtonRef}
      eventId={basicInfo.id}
      eventName={basicInfo.name}
      eventUrl={basicInfo.url}
      country={basicInfo.venue?.address?.country ?? 'US'}
      locale={locale}
    />
  ) : null;

  const relatedEventsComponent = basicInfo?.id ? (
    <RelatedEvents
      eventId={basicInfo.id}
      isBodyRendered={isBodyRendered}
      eventBasicInfo={basicInfo}
      sessionData={sessionData}
    />
  ) : null;

  const moreOrganizerEventsComponent =
    basicInfo?.id && basicInfo?.organizer?.id ? (
      <MoreOrganizerEvents
        eventId={basicInfo.id}
        isBodyRendered={isBodyRendered}
        eventBasicInfo={basicInfo}
        sessionData={sessionData}
      />
    ) : null;

  return (
    <SalesStatusProvider salesStatusData={salesStatus}>
      <div className={styles.bodyWrapper} data-testid="body" id="listing-body">
        <OrganizerFollowStatusProvider organizer={basicInfo.organizer}>
          <CreatorBanner isLoading={isLoading} canManageEvent={canManageEvent} />
          <EventHero
            hasCroppedLogo={false}
            mainBodyBackground={{ croppedLogoUrl50: gallery?.images?.[0]?.url }}
            items={carouselItems}
            featuredVideo={featuredVideo}
            nativeVideo={nativeVideo}
            eventName={basicInfo.name}
          />
          <EventSignalsBar />
          <div className={styles.contentContainer}>
            <div className={styles.layoutWrapper}>
              <Layout>
                <Main className={styles.mainContent}>
                  <EventDetails basicInfo={basicInfo} collections={collections} presenterInfo={presenterInfo} />
                  {viewDetailsButton}
                  <SectionWrapper name="overview">{overviewComponent}</SectionWrapper>
                  <SectionWrapper name="artistsInfo">{artistInfoComponent}</SectionWrapper>
                  <SectionWrapper name="goodToKnow">{goodToKnowComponent}</SectionWrapper>
                  <SectionWrapper name="location">{locationComponent}</SectionWrapper>
                  <SectionWrapper name="agenda">{agendaComponent}</SectionWrapper>
                  <SectionWrapper name="faqs" noDivider>
                    {faqsComponent}
                  </SectionWrapper>
                  <SectionWrapper name="organizerCard" noDivider>
                    {organizerCardComponent}
                  </SectionWrapper>
                  <SectionWrapper name="reportThisEvent">{reportThisEventButtonComponent}</SectionWrapper>
                </Main>
                {moreOrganizerEventsComponent}
                {relatedEventsComponent}
                <EventTags tags={tags} />
              </Layout>
            </div>
            <div ref={wrapperRef} className={styles.conversionBarWrapper}>
              <div ref={sentinelRef} className={styles.sentinel}></div>
              <ConversionBar isSticky={isStickyConversionBar} isPositionCalculated={isPositionCalculated} />
            </div>
          </div>
          <Footer
            refTargets={{
              'report-this-event-button': () => reportButtonRef.current?.triggerModal(),
            }}
          />
        </OrganizerFollowStatusProvider>
      </div>
    </SalesStatusProvider>
  );
};
